/// Sight reduction — converting a celestial sight into a position line.
///
/// Two routes:
///   - The general **intercept method** (Marcq Saint-Hilaire): from an
///     Assumed Position + the body's GHA + Dec, compute the altitude
///     and azimuth you'd expect (Hc, Zn). Compare with the corrected
///     observation Ho; the difference in arc-minutes is the intercept
///     in nautical miles. "Ho More, Towards" (HoMoTo): if Ho > Hc you
///     plot the intercept *towards* the body, else away.
///   - The **meridian-altitude** shortcut: at the moment a body
///     transits your meridian, latitude falls straight out of
///     `Lat = Dec ± (90° − Ho)` with the sign set by which side of you
///     the body bore at transit.
///
/// The spherical-triangle formulae used:
///
///   sin(Hc) = sin(Lat)·sin(Dec) + cos(Lat)·cos(Dec)·cos(LHA)
///   cos(Z)  = (sin(Dec) − sin(Hc)·sin(Lat)) / (cos(Hc)·cos(Lat))
///
/// `LHA = GHA + λ_E` (east longitude positive). True azimuth Zn comes
/// from Z by the hemisphere/LHA rule.

import { DEG, RAD, normalize360 } from "./time";

export interface SightInput {
  /// Assumed-position latitude, degrees (N positive, S negative).
  apLatDeg: number;
  /// Assumed-position longitude, degrees (E positive, W negative).
  apLonDeg: number;
  /// Body's Greenwich Hour Angle at the sight, degrees [0, 360).
  ghaDeg: number;
  /// Body's declination at the sight, degrees (N positive, S negative).
  decDeg: number;
  /// Optional observed altitude (Ho) — when present, the result also
  /// carries the intercept distance and direction.
  hoDeg?: number;
}

export interface SightSolution {
  /// Local Hour Angle of the body, degrees [0, 360).
  lhaDeg: number;
  /// Computed altitude at the assumed position, degrees [−90, 90].
  hcDeg: number;
  /// True azimuth of the body from the AP, degrees [0, 360).
  znDeg: number;
  /// Intercept magnitude in nautical miles (= arc-minutes of Ho−Hc).
  /// Only set when `hoDeg` was supplied.
  interceptNm?: number;
  /// "Towards" the body if Ho > Hc, "Away" otherwise.
  /// Only set when `hoDeg` was supplied.
  direction?: "Towards" | "Away";
}

function clampUnit(x: number): number {
  return Math.max(-1, Math.min(1, x));
}

export function sightReduction(input: SightInput): SightSolution {
  const lat = input.apLatDeg * DEG;
  const dec = input.decDeg * DEG;
  const lhaDeg = normalize360(input.ghaDeg + input.apLonDeg);
  const lha = lhaDeg * DEG;

  const sinHc =
    Math.sin(lat) * Math.sin(dec) +
    Math.cos(lat) * Math.cos(dec) * Math.cos(lha);
  const hc = Math.asin(clampUnit(sinHc));
  const hcDeg = hc * RAD;

  // Z is the azimuth angle in [0, 180], from N or S meridian.
  const cosZ =
    (Math.sin(dec) - Math.sin(hc) * Math.sin(lat)) /
    (Math.cos(hc) * Math.cos(lat));
  const zDeg = Math.acos(clampUnit(cosZ)) * RAD;

  // Convert Z to true azimuth Zn. The Z formula above returns the
  // angle from the **celestial north pole** regardless of the
  // observer's hemisphere (signed Lat already does the work), so the
  // same quadrant rule applies to both: body west of meridian
  // (LHA < 180°) → Zn = 360° − Z; body east (LHA > 180°) → Zn = Z.
  const znDeg = normalize360(lhaDeg > 180 ? zDeg : 360 - zDeg);

  const out: SightSolution = { lhaDeg, hcDeg, znDeg };

  if (input.hoDeg !== undefined) {
    const a = (input.hoDeg - hcDeg) * 60; // 1' altitude = 1 nm
    out.interceptNm = Math.abs(a);
    out.direction = a >= 0 ? "Towards" : "Away";
  }
  return out;
}

/// Intercept on its own — given Ho and Hc, returns the distance in
/// nautical miles and "Towards" / "Away" (HoMoTo: Ho More → Towards).
export function intercept(
  hoDeg: number,
  hcDeg: number,
): { distanceNm: number; direction: "Towards" | "Away" } {
  const a = (hoDeg - hcDeg) * 60;
  return { distanceNm: Math.abs(a), direction: a >= 0 ? "Towards" : "Away" };
}

export interface MeridianAltitudeInput {
  /// Corrected observed altitude at the moment of transit.
  hoDeg: number;
  /// Body's declination at transit, degrees (N positive).
  decDeg: number;
  /// At transit, did the body bear NORTH of the observer (i.e. the
  /// observer is south of the body's GP) or SOUTH (the usual case
  /// when the Sun crosses the meridian for a UK navigator)?
  bodyBearing: "N" | "S";
}

/// Latitude from a meridian-altitude (noon-sun) sight.
///
///   Body bears South:   Lat = Dec + (90° − Ho)
///   Body bears North:   Lat = Dec − (90° − Ho)
///
/// Declination keeps its sign (N positive); the result is signed
/// likewise (positive = N latitude).
export function latitudeFromMeridianAltitude(
  input: MeridianAltitudeInput,
): number {
  const zenithDistanceDeg = 90 - input.hoDeg;
  return input.bodyBearing === "S"
    ? input.decDeg + zenithDistanceDeg
    : input.decDeg - zenithDistanceDeg;
}

/// ───────────────────────────────────────────────────────────────────
/// RYA-syllabus alternate methods.
///
/// The two formulas above (sin-Hc / cos-Z) are the modern compact form.
/// The RYA Ocean syllabus still teaches the equivalent table-based
/// methods — the **haversine** formula for the altitude and the
/// **ABC** lookup for the azimuth. They are mathematically identical
/// to the spherical-triangle solution and produce the same answers to
/// floating-point precision; what differs is the arithmetic the
/// student is expected to be able to do on paper with Norie's or
/// Burton's tables.
/// ───────────────────────────────────────────────────────────────────

export interface HaversineSolution {
  /// L − D, degrees (signed).
  latMinusDecDeg: number;
  /// hav(L − D).
  havLatMinusDec: number;
  /// hav(LHA).
  havLha: number;
  /// cos(L) · cos(D).
  cosLatCosDec: number;
  /// hav(ZD) = hav(L − D) + cos(L)·cos(D)·hav(LHA).
  havZd: number;
  /// Zenith distance ZD, degrees [0, 180].
  zdDeg: number;
  /// Computed altitude Hc = 90° − ZD, degrees.
  hcDeg: number;
}

/// Haversine sight reduction for the altitude:
///
///   hav(ZD) = hav(L − D) + cos(L) · cos(D) · hav(LHA)
///   ZD      = 90° − Hc
///   hav(x)  = sin²(x/2) = (1 − cos x) / 2
///
/// Algebraically identical to `sin(Hc) = sin(L)·sin(D) + cos(L)·cos(D)·cos(LHA)`,
/// but the haversine form is what Norie's tables tabulate so the
/// student can finish the work without a calculator.
export function haversineHc(input: {
  latDeg: number;
  decDeg: number;
  lhaDeg: number;
}): HaversineSolution {
  const L = input.latDeg * DEG;
  const D = input.decDeg * DEG;
  const LMD = input.latDeg - input.decDeg;
  const havLD = (1 - Math.cos(LMD * DEG)) / 2;
  const havLha = (1 - Math.cos(input.lhaDeg * DEG)) / 2;
  const cosLD = Math.cos(L) * Math.cos(D);
  const havZd = Math.min(1, Math.max(0, havLD + cosLD * havLha));
  const zdDeg = 2 * Math.asin(Math.sqrt(havZd)) * RAD;
  return {
    latMinusDecDeg: LMD,
    havLatMinusDec: havLD,
    havLha,
    cosLatCosDec: cosLD,
    havZd,
    zdDeg,
    hcDeg: 90 - zdDeg,
  };
}

export interface AbcSolution {
  /// A = |tan(Lat) / tan(LHA)| and its N/S name.
  a: number;
  aName: "N" | "S";
  /// B = |tan(Dec) / sin(LHA)| and its N/S name.
  b: number;
  bName: "N" | "S";
  /// C = combined value (signed via the names) and its N/S name.
  c: number;
  cName: "N" | "S";
  /// Azimuth angle Z, degrees [0, 90].
  zDeg: number;
  /// Z's N/S name (same as C).
  zNS: "N" | "S";
  /// Z's E/W name (E if LHA > 180°, else W).
  zEW: "E" | "W";
  /// True azimuth Zn, degrees [0, 360).
  znDeg: number;
}

/// ABC-tables azimuth — the RYA Ocean syllabus method for finding the
/// body's true bearing without trig. The student looks up A from a
/// Lat/LHA table, B from a Dec/LHA table, combines them by name
/// rules to get C, then reads Z from a C/Lat table; here we do the
/// equivalent arithmetic directly so the calculator can show every
/// intermediate value.
///
/// Naming rules (Norie's convention):
///
///   - A is named opposite to Lat, unless LHA is in (90°, 270°) in
///     which case A is named the same as Lat.
///   - B is named the same as Dec.
///   - C: combine A and B as signed numbers (N positive, S negative);
///     |C| with the sign's N/S name.
///   - Z = atan(1 / (|C| · cos Lat)), 0..90°.
///   - Z is named (N or S) the same as C; (E or W) is W if LHA < 180°
///     else E.
///   - Zn from the (N/S, E/W) bearing using the standard quadrant
///     conversion.
export function abcAzimuth(input: {
  latDeg: number;
  decDeg: number;
  lhaDeg: number;
}): AbcSolution {
  const latIsN = input.latDeg >= 0;
  const decIsN = input.decDeg >= 0;
  const absLat = Math.abs(input.latDeg);
  const absDec = Math.abs(input.decDeg);
  const lha = input.lhaDeg;

  const tanAbsLat = Math.tan(absLat * DEG);
  const tanAbsDec = Math.tan(absDec * DEG);
  const tanLha = Math.tan(lha * DEG);
  const sinLha = Math.sin(lha * DEG);

  const aMag = Math.abs(tanAbsLat / tanLha);
  const aName: "N" | "S" =
    lha > 90 && lha < 270 ? (latIsN ? "N" : "S") : latIsN ? "S" : "N";

  const bMag = Math.abs(tanAbsDec / sinLha);
  const bName: "N" | "S" = decIsN ? "N" : "S";

  const aSigned = aName === "N" ? aMag : -aMag;
  const bSigned = bName === "N" ? bMag : -bMag;
  const cSigned = aSigned + bSigned;
  const cMag = Math.abs(cSigned);
  const cName: "N" | "S" = cSigned >= 0 ? "N" : "S";

  const zRad = Math.atan(1 / (cMag * Math.cos(absLat * DEG)));
  const zDeg = zRad * RAD;
  const zNS: "N" | "S" = cName;
  const zEW: "E" | "W" = lha < 180 ? "W" : "E";

  let znDeg: number;
  if (zNS === "N" && zEW === "E") znDeg = zDeg;
  else if (zNS === "N" && zEW === "W") znDeg = 360 - zDeg;
  else if (zNS === "S" && zEW === "E") znDeg = 180 - zDeg;
  else znDeg = 180 + zDeg;

  return {
    a: aMag,
    aName,
    b: bMag,
    bName,
    c: cMag,
    cName,
    zDeg,
    zNS,
    zEW,
    znDeg: normalize360(znDeg),
  };
}
